// The anchoring recipe kit: the input every anchoring look draws from, and
// the arithmetic it draws with (R1125, `ui-map-interface.md` §6.1.5).
//
// An ANCHORING look is what a hull draws while its anchor goes down
// (SETTING), holds (SET) and comes up (WEIGHING), numbers.md §16. The World
// theme field `anchoring` names one of eleven, the cards the owner picked from
// `archive/mockups/056-anchor-cycle.html`. `anchor-styles.ts` is the registry
// that answers a look id; `anchor-recipes-<group>.ts` hold the draw code. This
// module is what all of them import.
//
// This is a leaf (`leaves.test.ts`). It names the three sinks of a layer
// through a type-only clause, the way `gather-recipe-kit.ts` does. The fleet
// layer owns the state, the clock, the cull, the atlas and the pools, and it
// hands them in as ONE reused context a hull. The clamps and the eases every
// family shares stand in `recipe-kit.ts`; the primitives here are the kit's
// own, because an anchoring stroke passes `thin` and takes the round cap
// (R1132), and the context carries no fade.
//
// **Nothing here allocates on the frame path.** A recipe runs once per anchor
// hull a frame (§10.1). The text formatter keeps what it built.
//
// **A recipe is class-blind.** The context carries no hull class, so a look
// reads the anchor state and never the class, and a later anchor hull takes
// every look with no change. The one hull-bound reading is the beam order
// of a Beacon (R2858), and it comes off the wire (`FleetState.beam_left_sec`),
// never off the class.
//
// ---------------------------------------------------------------------------
// HOW A MOCKUP CARD MAPS ONTO THE KIT
// ---------------------------------------------------------------------------
//
// The cards of round 056 draw in WORLD px with the hull at the origin, on a
// canvas that is NOT turned: `hull(c, S)` turns inside its own save. The
// translation is fixed:
//
//   | the card writes                 | the recipe writes                    |
//   | ------------------------------- | ------------------------------------ |
//   | the canvas origin               | `c.x` / `c.y`, added by every call   |
//   | `S.head`                        | `c.head`                             |
//   | `S.state` SETTING, SET, WEIGHING| `c.phase`                            |
//   | `S.p`                           | `c.p`, the share of cable out        |
//   | `S.lift` (null off a Beacon)    | `c.beam`, −1 where there is none     |
//   | `S.k` in SET                    | `c.since`, WALL seconds              |
//   | `S.ink`                         | `c.ink`: gold or the faction ink     |
//   | `S.a`                           | `c.a`: 1, or `other_alpha`           |
//   | `S.own`                         | `c.own`                              |
//   | `S.z`                           | `c.z`                                |
//   | `S.i` (a phase offset)          | `c.seed`, a stable number a hull     |
//   | `circle(c, …)`                  | `ring(c, …)`                         |
//   | `disc(c, …)`                    | `disc(c, …)`                         |
//   | `arc(c, …)` (a start and an END)| `arcOf(c, …)` (a start and a SWEEP)  |
//   | `line(c, …)`                    | `line(c, …)`                         |
//   | a filled square turned 45°      | `diamondFill(c, …)`                  |
//   | `text(c, …)` of numerals        | `c.text.run(…)`                      |
//
// Every FIGURE came out of the card and stands in the look's row of
// `data/presentation/anchor.toml`. Every RATIO stays in the recipe file as a
// named constant, the rule the gather recipes follow (R1120).

import { ANCHOR_LIMITS, ANCHOR_LOOKS } from '../gen/presentation';
import type { AnchorRow } from '../gen/presentation';
import type {
  CircleSink, QuadSink, SegmentSink, SpriteEntry,
} from '../fast/layers/batches';
import { SOLID_KEY, clamp01, fillFor, strokeFor } from './recipe-kit';
import { leftKey, leftSpan } from '../locale/span';

export type { AnchorRow };
export {
  DEEP_INK, TAU, WATER_INK, bezier2, clamp01, ease, lerp, type Pt,
} from './recipe-kit';
/** A cubic that starts fast and settles. It is `easeOut` of the page. */
export { outCubic as easeOut } from './recipe-kit';

// ---------------------------------------------------------------------------
// The inks
// ---------------------------------------------------------------------------
//
// The canvas is never themed (R1007). A look wears the cable ink the layer
// hands in, the shared kit's `WATER_INK` for the core of a hollow buoy, dot
// or pin hole, and its `DEEP_INK` for the ground.

// ---------------------------------------------------------------------------
// The context
// ---------------------------------------------------------------------------

/** The three states a look draws. Under way draws nothing. */
export type AnchorPhase = 'setting' | 'set' | 'weighing';

/** What `since` reads for a state the client never saw begin: a hull that
 *  came into view already SET shows no landing beat. */
export const ANCHOR_SINCE_UNSEEN = 1e6;

/**
 * Where a run of numerals goes (card 19).
 *
 * The fleet layer answers it with the damage figure's own glyph cells (R538),
 * so the map bakes no second numeral face. The run is LEFT aligned and its
 * middle stands on the line.
 */
export interface AnchorTextSink {
  /**
   * One run, anchored at the world point `(x, y)` and offset by `(dx, dy)`
   * SCREEN px, its glyphs `px` SCREEN px tall at every zoom.
   */
  run(
    text: string, x: number, y: number, dx: number, dy: number,
    px: number, ink: number, alpha: number,
  ): void;
}

/**
 * ONE ANCHOR HULL, as a recipe reads it. The layer reuses ONE of these
 * (R131).
 *
 * Every length is world px and every angle is radians, with +x east and +y
 * south. It carries NO hull class, on purpose.
 */
export interface AnchorCtx {
  /** The layer's clock in WALL seconds. A pulse and a march read it. */
  t: number;
  /** The camera scale: CSS px for one world px. */
  z: number;
  /** The figure row of the look that draws. */
  row: AnchorRow;
  /** The engine's round-cap flag (`fast/layer.ts` `F_ROUND`), handed in so
   *  this leaf takes no engine import. */
  round: number;

  /** The hull's centre, in world px. */
  x: number;
  y: number;
  /** The hull's heading, radians. */
  head: number;

  /** The state of the cable. */
  phase: AnchorPhase;
  /**
   * 0 to 1, the share of cable out: it fills while the cable goes down,
   * stands at 1 while it is SET and drains while it comes up. The layer
   * reads it off `anchor_left_sec` and `Catalog.anchor_cycle_sec`.
   */
  p: number;
  /**
   * 0 to 1, the share of the beam order still to run, or −1 where the
   * hull has none (R1125, R2858). Only a SET hull whose `beam_left_sec` is
   * above zero carries one; a tower, and a Beacon whose beam ended, read
   * −1. It comes off the wire, never off the class.
   */
  beam: number;
  /**
   * The WORLD seconds left: of the cycle while SETTING or WEIGHING, and of
   * the beam order while SET (0 with no beam). It went through
   * `GameState.mapToWorldSec`, as `ui/format.ts anchorLabel` does.
   */
  leftSec: number;
  /** The WALL seconds since this state began on this client, or
   *  `ANCHOR_SINCE_UNSEEN`. */
  since: number;

  /** True on your own hull. */
  own: boolean;
  /** The cable ink: the gold of §6.1 on your own hull, else the faction ink. */
  ink: number;
  /** 1 on your own hull, `ANCHOR_LIMITS.otherAlpha` on another faction's. */
  a: number;
  /** A stable number a hull, for a phase offset (the card's `S.i`). */
  seed: number;

  quads: QuadSink;
  segs: SegmentSink;
  rings: CircleSink;
  /** An atlas cell by key. */
  sprite(key: string): SpriteEntry;
  /** The numeral sink. */
  text: AnchorTextSink;
}

/** ONE ANCHORING LOOK: what it draws for one anchor hull in one frame. */
export type AnchorRecipe = (c: AnchorCtx) => void;

/** The `a` of a hull: full ink on your own, the fainter alpha elsewhere. */
export function anchorAlpha(own: boolean): number {
  return own ? 1 : ANCHOR_LIMITS.otherAlpha;
}

// ---------------------------------------------------------------------------
// The arithmetic
// ---------------------------------------------------------------------------

/** Twelve o'clock, where an arc of a dial starts and tick 0 stands. */
export const TWELVE = -Math.PI / 2;

/** 0 to 1 across `[a, b]` of `v`, clamped. It is `span` of the page. */
export function span(v: number, a: number, b: number): number {
  return b > a ? clamp01((v - a) / (b - a)) : v >= b ? 1 : 0;
}

/**
 * The landing beat of a SET cable: 1 at the moment the cable went SET,
 * falling to 0 over the row's `flash_sec`. It is the card's
 * `1 - span(S.k, 0, flash)`, and it reads 0 on a hull that came into view
 * already SET.
 */
export function flashOf(c: AnchorCtx): number {
  if (c.phase !== 'set' || c.row.flashSec <= 0) return 0;
  return 1 - span(c.since, 0, c.row.flashSec);
}

// -- the numerals ------------------------------------------------------------

const spanCache = new Map<number, string>();

/**
 * A count of seconds with its units (R2741): `45s` under a minute, `2m 5s`
 * under an hour and `3h 12m` from one hour on, the one span form of
 * `locale/span.ts`. It is `D_clock` and `D_hours` of the page. The text is
 * kept by the step its words move by, so a frame builds none.
 */
export function spanFigure(sec: number): string {
  const k = leftKey(sec);
  let t = spanCache.get(k);
  if (t === undefined) {
    t = leftSpan(k);
    spanCache.set(k, t);
  }
  return t;
}

// ---------------------------------------------------------------------------
// The primitives
// ---------------------------------------------------------------------------
//
// Each one clamps its alpha under the ceiling of `[anchor.limits]` and skips
// a mark that would draw nothing, so a recipe states the card's figure and
// never guards it.

/** A fill alpha, held under the ceiling. */
export const fillAlpha = fillFor(ANCHOR_LIMITS);

/** A stroke alpha, held under the ceiling. */
export const strokeAlpha = strokeFor(ANCHOR_LIMITS);

/** A hard filled circle. It is `disc` of the page. */
export function disc(
  c: AnchorCtx, x: number, y: number, r: number, ink: number, a: number,
): void {
  const al = fillAlpha(a);
  if (r <= 0 || al <= 0) return;
  c.rings.disc(x, y, r, ink, al);
}

/**
 * The drawn width of a stroke the card gave as `w` (R1132). A stroke up to
 * `heavyFromPx` keeps its width; a heavier one keeps `heavyFromPx` plus
 * `heavyKeep` of the part above it. `line`, `ring` and `arcOf` pass every
 * width through it, so every look of the batch thins its heavy strokes and
 * keeps its faint ones.
 */
export function thin(w: number): number {
  const t = ANCHOR_LIMITS.heavyFromPx;
  return w <= t ? w : t + (w - t) * ANCHOR_LIMITS.heavyKeep;
}

/**
 * The width to pass for a stroke that FILLS a shape, so that `thin` gives
 * back `w` and the fill stays whole (R1132). It is the inverse of `thin`.
 */
export function solidWidth(w: number): number {
  const t = ANCHOR_LIMITS.heavyFromPx;
  return w <= t ? w : t + (w - t) / ANCHOR_LIMITS.heavyKeep;
}

/**
 * The stroke gain at a far zoom, the cards' `B_w`: under zoom 1 a stroke
 * keeps `1 / zoom^grow` of its width, so it still reads at zoom 0.5. The
 * cards write `1 / sqrt(zoom)`, which is `grow = 0.5` in the row.
 */
export function strokeGain(c: AnchorCtx): number {
  return c.z < 1 && c.row.grow > 0 ? Math.pow(c.z, -c.row.grow) : 1;
}

/** A stroked full circle. It is `circle` of the page with no dash. */
export function ring(
  c: AnchorCtx, x: number, y: number, r: number, w: number, ink: number, a: number,
): void {
  const al = strokeAlpha(a);
  if (r <= 0 || w <= 0 || al <= 0) return;
  c.rings.ring(x, y, r, thin(w), ink, al);
}

/**
 * The faint backing ring of ANCHOR RING, on a SET hull only (R1130). A look
 * whose own mark stands beside the hull calls it, so a SET hull still reads
 * as anchored inside a busy fight. It takes the radius, the width and the two
 * alphas of the `anchor-ring` row, so it is that ring stroke for stroke.
 */
export function setRing(c: AnchorCtx): void {
  if (c.phase !== 'set') return;
  const r = ANCHOR_LOOKS['anchor-ring'];
  ring(c, c.x, c.y, r.rPx, r.width3Px, c.ink, c.own ? r.alpha : r.alpha2);
}

/** A stroked partial circle from `start` over `sweep` radians. The page's
 *  `arc` takes an END, so a port passes `a1 - a0`. */
export function arcOf(
  c: AnchorCtx, x: number, y: number, r: number, w: number,
  start: number, sweep: number, ink: number, a: number,
): void {
  const al = strokeAlpha(a);
  if (r <= 0 || w <= 0 || sweep === 0 || al <= 0) return;
  c.rings.arc(x, y, r, thin(w), start, sweep, ink, al);
}

/** A straight stroke. `round` asks for the card's round caps. It is `line`
 *  of the page. */
export function line(
  c: AnchorCtx, x0: number, y0: number, x1: number, y1: number,
  w: number, ink: number, a: number, round = true, dashOn = 0, dashOff = 0,
): void {
  const al = strokeAlpha(a);
  if (w <= 0 || al <= 0) return;
  c.segs.line(x0, y0, x1, y1, thin(w), ink, al, round ? c.round : 0, dashOn, dashOff);
}

/**
 * A square of half diagonal `d` turned 45°, filled: a diamond with its
 * points up, right, down and left. It is the buoy of card 19. The quad sink
 * draws the one flat texel under the turn.
 */
export function diamondFill(
  c: AnchorCtx, x: number, y: number, d: number, ink: number, a: number,
): void {
  const al = fillAlpha(a);
  if (d <= 0 || al <= 0) return;
  const e = c.sprite(SOLID_KEY);
  const side = d * Math.SQRT2;
  c.quads.quad(x, y, 0, 0, side, side, Math.PI / 4, 0, ink, al, e.u0, e.v0, e.u1, e.v1);
}

/** The same diamond as an outline: four strokes, top, right, bottom, left. */
export function diamondOutline(
  c: AnchorCtx, x: number, y: number, d: number, w: number, ink: number, a: number,
): void {
  if (d <= 0) return;
  line(c, x, y - d, x + d, y, w, ink, a);
  line(c, x + d, y, x, y + d, w, ink, a);
  line(c, x, y + d, x - d, y, w, ink, a);
  line(c, x - d, y, x, y - d, w, ink, a);
}
